import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { quickAuthenticateUserById, supabase, Tables } from "../utils/supabase";
import toast from "react-hot-toast";

const IDENTITY_STORAGE_KEY = "qa_active_identity";
const INACTIVITY_TIMEOUT_MS = 90_000; // 90 seconds

interface IdentifiedUser {
  userId: string;
  fullName: string;
  twoDigitId: string;
}

interface StoredIdentity {
  user: IdentifiedUser;
  timestamp: number;
}

interface UserIdentityContextType {
  identifiedUser: IdentifiedUser | null;
  isIdentified: boolean;
  identifyUser: (twoDigitId: string) => Promise<{ success: boolean; error?: string }>;
  clearIdentity: () => void;
}

const UserIdentityContext = createContext<UserIdentityContextType | undefined>(undefined);

export function useUserIdentity() {
  const ctx = useContext(UserIdentityContext);
  if (!ctx) {
    throw new Error("useUserIdentity must be used within a UserIdentityProvider");
  }
  return ctx;
}

function readStoredIdentity(): StoredIdentity | null {
  try {
    const raw = sessionStorage.getItem(IDENTITY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredIdentity;
    if (parsed?.user?.userId && parsed?.timestamp) return parsed;
    return null;
  } catch {
    return null;
  }
}

function writeStoredIdentity(user: IdentifiedUser): void {
  const stored: StoredIdentity = { user, timestamp: Date.now() };
  sessionStorage.setItem(IDENTITY_STORAGE_KEY, JSON.stringify(stored));
}

function touchStoredIdentity(): void {
  const stored = readStoredIdentity();
  if (stored) {
    stored.timestamp = Date.now();
    sessionStorage.setItem(IDENTITY_STORAGE_KEY, JSON.stringify(stored));
  }
}

function clearStoredIdentity(): void {
  sessionStorage.removeItem(IDENTITY_STORAGE_KEY);
}

export const UserIdentityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, quickSwitchUser } = useAuth();
  const [identifiedUser, setIdentifiedUser] = useState<IdentifiedUser | null>(null);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // On mount / when auth user changes: check for stored identity
  useEffect(() => {
    if (!user) {
      // User signed out — clear identity
      setIdentifiedUser(null);
      clearStoredIdentity();
      return;
    }

    const stored = readStoredIdentity();
    if (stored && Date.now() - stored.timestamp < INACTIVITY_TIMEOUT_MS) {
      setIdentifiedUser(stored.user);
    } else {
      // Expired or no stored identity
      setIdentifiedUser(null);
      clearStoredIdentity();
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Inactivity timer
  useEffect(() => {
    if (!identifiedUser || !user) return;

    function resetTimer() {
      touchStoredIdentity();
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      inactivityTimerRef.current = setTimeout(() => {
        setIdentifiedUser(null);
        clearStoredIdentity();
      }, INACTIVITY_TIMEOUT_MS);
    }

    const events = [
      "mousedown", "keydown", "scroll", "touchstart", "mousemove",
      "click", "input", "change", "focus", "pointerdown",
    ];
    events.forEach((e) => window.addEventListener(e, resetTimer));
    resetTimer(); // start timer

    return () => {
      events.forEach((e) => window.removeEventListener(e, resetTimer));
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [identifiedUser, user]);

  const identifyUser = useCallback(async (twoDigitId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const authResult = await quickAuthenticateUserById(twoDigitId);

      if (!authResult.success || !authResult.user_id) {
        return { success: false, error: authResult.error_message || "Onbekend personeelsnummer" };
      }

      // Fetch user name
      const { data: userData, error: userError } = await supabase
        .from(Tables.USERS)
        .select("full_name")
        .eq("id", authResult.user_id)
        .single();

      if (userError || !userData) {
        return { success: false, error: "Gebruiker niet gevonden" };
      }

      // Switch auth context to this user
      const switchResult = await quickSwitchUser(authResult.user_id);
      if (switchResult.error) {
        return { success: false, error: switchResult.error.message };
      }

      const identified: IdentifiedUser = {
        userId: authResult.user_id,
        fullName: userData.full_name,
        twoDigitId,
      };

      setIdentifiedUser(identified);
      writeStoredIdentity(identified);
      toast.success(`Welkom, ${userData.full_name}`);
      return { success: true };
    } catch (err) {
      console.error("Identification error:", err);
      return { success: false, error: "Er is een fout opgetreden" };
    }
  }, [quickSwitchUser]);

  const clearIdentity = useCallback(() => {
    setIdentifiedUser(null);
    clearStoredIdentity();
  }, []);

  return (
    <UserIdentityContext.Provider
      value={{
        identifiedUser,
        isIdentified: identifiedUser !== null,
        identifyUser,
        clearIdentity,
      }}
    >
      {children}
    </UserIdentityContext.Provider>
  );
};
